/**
 * Jornada de campo por encuestador.
 *
 * Responde dos preguntas que el panel no contestaba:
 *
 *  1. Qué levantó cada quien CADA DÍA, contando las puertas que no abrieron.
 *     El avance sólo mostraba entrevistas completas, así que un día de ocho
 *     rechazos se veía igual que un día sin salir —y son cosas opuestas: una
 *     es trabajo duro en una zona difícil y la otra es ausencia—.
 *  2. Cuánto tiempo estuvo en calle. Con la salvedad, que la pantalla repite
 *     donde hace falta, de que eso se ACOTA, no se mide: nadie ficha.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { STUDY_TIMEZONE } from '@capi/shared';

import { api, type WorkloadInterviewer } from '../api/client';
import { BarChart, StatusChip, Tile, fmtDecimal, fmtInt } from '../components/charts';

/** Minutos a «3 h 20 m», que es como se lee una jornada. */
function horas(min: number): string {
  if (min <= 0) return '—';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')} m` : `${m} m`;
}

const DISPOSITION_LABEL: Record<string, string> = {
  entrevista_completa: 'Entrevista completa',
  entrevista_parcial: 'Entrevista parcial',
  rechazo: 'Rechazo',
  nadie_en_casa: 'Nadie en casa',
  seleccionado_ausente: 'Seleccionado ausente',
  no_elegible: 'No elegible',
  vivienda_deshabitada: 'Vivienda deshabitada',
  no_accesible: 'No accesible',
};

export function WorkloadPage() {
  const [abierto, setAbierto] = useState<string | null>(null);

  const datos = useQuery({
    queryKey: ['monitoring', 'workload'],
    queryFn: () => api.workload(),
  });

  const totales = useMemo(() => {
    const xs = datos.data?.interviewers ?? [];
    return {
      enCalle: xs.filter((x) => x.totals.daysWorked > 0).length,
      completadas: xs.reduce((a, x) => a + x.totals.completed, 0),
      puertas: xs.reduce((a, x) => a + x.totals.doors, 0),
      jornada: xs.reduce((a, x) => a + x.totals.spanMinutes, 0),
    };
  }, [datos.data]);

  if (datos.isLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }
  if (datos.isError || !datos.data) {
    return <div className="callout callout--danger">No se pudo cargar la jornada de campo.</div>;
  }

  const { interviewers, maxGapMinutes } = datos.data;
  const exito = totales.puertas > 0 ? (totales.completadas / totales.puertas) * 100 : 0;

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Jornada de campo</h1>
        <p className="page-sub">
          Producción diaria por encuestador, incluidas las puertas que no produjeron entrevista
          (§9.1)
        </p>
      </div>

      <div className="grid grid--4">
        <Tile label="Encuestadores en calle" value={fmtInt(totales.enCalle)} reference="§9.1" />
        <Tile label="Entrevistas completas" value={fmtInt(totales.completadas)} />
        <Tile
          label="Puertas tocadas"
          value={fmtInt(totales.puertas)}
          meta={`${fmtDecimal(exito, 1)}% terminó en entrevista`}
        />
        <Tile label="Jornada acumulada" value={horas(totales.jornada)} />
      </div>

      {/**
       * La advertencia va arriba porque la cifra de tiempo es la que más se
       * presta a usarse para evaluar a una persona, y es la que menos
       * precisión tiene.
       */}
      <div className="callout callout--info">
        <span className="callout__title">Cómo leer el tiempo</span>
        Nadie ficha: lo que el sistema tiene son marcas de tiempo de entrevistas y visitas, así que
        la jornada se <strong>acota</strong> con tres cifras en lugar de medirse con una.{' '}
        <strong>Jornada</strong> va de la primera marca a la última e incluye comida y traslados.{' '}
        <strong>Activo</strong> descuenta los huecos de más de {maxGapMinutes} minutos, que se
        parecen más a una pausa que a caminar entre manzanas. <strong>Entrevistas</strong> es sólo
        el tiempo dentro del cuestionario. La verdad está entre las dos últimas.
      </div>

      {interviewers.map((x) => (
        <InterviewerCard
          key={x.userId}
          interviewer={x}
          abierto={abierto === x.userId}
          onToggle={() => setAbierto(abierto === x.userId ? null : x.userId)}
        />
      ))}
    </>
  );
}

/* -------------------------------------------------------------------------- */

function InterviewerCard({
  interviewer: x,
  abierto,
  onToggle,
}: {
  interviewer: WorkloadInterviewer;
  abierto: boolean;
  onToggle: () => void;
}) {
  const sinSalir = x.totals.daysWorked === 0;
  const exito =
    x.totals.doors > 0 ? (x.totals.completed / x.totals.doors) * 100 : null;

  return (
    <div className="panel">
      <div className="panel__head">
        <div>
          <h2 className="panel__title">
            <span className="mono">{x.code}</span> · {x.name}{' '}
            {!x.isActive ? <StatusChip status="neutral">Baja</StatusChip> : null}
          </h2>
          <p className="panel__note">
            {sinSalir ? (
              'Sin registros de campo todavía.'
            ) : (
              <>
                {fmtInt(x.totals.completed)} completas
                {x.totals.partial > 0 ? ` · ${fmtInt(x.totals.partial)} parciales` : ''} ·{' '}
                {fmtInt(x.totals.doors)} puertas en {fmtInt(x.totals.daysWorked)} día
                {x.totals.daysWorked === 1 ? '' : 's'}
                {exito !== null ? ` · ${fmtDecimal(exito, 0)}% de éxito` : ''}
              </>
            )}
          </p>
        </div>
        {!sinSalir ? (
          <button type="button" className="btn btn--ghost btn--sm" onClick={onToggle}>
            {abierto ? 'Ocultar días' : 'Ver días'}
          </button>
        ) : null}
      </div>

      {sinSalir ? null : (
        <>
          <div className="row row--wrap" style={{ gap: 24, marginBottom: 12 }}>
            <Tile label="Jornada" value={horas(x.totals.spanMinutes)} />
            <Tile label="Activo" value={horas(x.totals.activeMinutes)} />
            <Tile label="En entrevista" value={horas(x.totals.interviewMinutes)} />
          </div>

          {/**
           * La gráfica diaria compara completas contra puertas tocadas. Es la
           * lectura que distingue un día flojo de un día duro: ocho puertas y
           * una entrevista es una zona difícil, no una persona lenta.
           */}
          <BarChart
            rows={x.days
              .slice()
              .reverse()
              .map((d) => ({
                key: d.day,
                // Mediodía para que el formateo no cruce el día en ningún huso.
                label: new Date(`${d.day}T12:00:00`).toLocaleDateString('es-MX', {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                }),
                value: d.completed,
                target: d.doors,
                valueLabel: `${fmtInt(d.completed)} de ${fmtInt(d.doors)}`,
                tooltip:
                  `${d.day}: ${fmtInt(d.completed)} completas de ${fmtInt(d.doors)} puertas · ` +
                  `jornada ${horas(d.time.spanMinutes)}, activo ${horas(d.time.activeMinutes)}`,
              }))}
            showTargetLegend
          />
          <p className="faint" style={{ marginTop: 6 }}>
            La barra es lo completado; la marca, las puertas tocadas ese día.
          </p>

          {abierto ? <DayTable days={x.days} /> : null}
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function DayTable({ days }: { days: WorkloadInterviewer['days'] }) {
  /**
   * Hora de TECATE, no la del navegador de quien mira.
   *
   * El coordinador puede estar en Ciudad de México, dos husos por delante:
   * sin fijar la zona, una entrada a las 09:00 de campo se leería como las
   * 11:00 y la jornada parecería empezar a media mañana.
   */
  const hora = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleTimeString('es-MX', {
          timeZone: STUDY_TIMEZONE,
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  return (
    <div className="table-wrap" style={{ marginTop: 16 }}>
      <table className="table">
        <thead>
          <tr>
            <th>Día</th>
            <th>Entrada</th>
            <th>Salida</th>
            <th>Jornada</th>
            <th>Activo</th>
            <th>Entrevista</th>
            <th className="num">Completas</th>
            <th className="num">Puertas</th>
            <th>Disposiciones</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.day}>
              <td className="strong">
                {new Date(`${d.day}T12:00:00`).toLocaleDateString('es-MX', {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                })}
              </td>
              <td className="faint">{hora(d.time.firstAt)}</td>
              <td className="faint">{hora(d.time.lastAt)}</td>
              <td>
                {horas(d.time.spanMinutes)}
                {/**
                 * Si se recortaron muchos huecos, la estimación de «activo»
                 * descansa sobre más supuesto que dato y conviene decirlo
                 * donde se lee la cifra, no en una nota general.
                 */}
                {d.time.cappedGaps > 2 ? (
                  <div className="faint" title={`${d.time.cappedGaps} huecos largos recortados`}>
                    {d.time.cappedGaps} pausas
                  </div>
                ) : null}
              </td>
              <td>{horas(d.time.activeMinutes)}</td>
              <td className="faint">{horas(d.time.interviewMinutes)}</td>
              <td className="num strong">{fmtInt(d.completed)}</td>
              <td className="num">{fmtInt(d.doors)}</td>
              <td>
                <div className="row row--wrap" style={{ gap: 4 }}>
                  {Object.entries(d.dispositions)
                    .sort((a, b) => b[1] - a[1])
                    .map(([code, n]) => (
                      <span key={code} className="chip" title={DISPOSITION_LABEL[code] ?? code}>
                        {(DISPOSITION_LABEL[code] ?? code).replace('Entrevista ', '')} {n}
                      </span>
                    ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
